// src/pages/ROICalculator/ResultsDisplay.jsx

import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Divider,
  Button,
  Chip,
  Alert
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as RevenueIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { CALCULATION_CONFIG } from './constants';

const ResultsDisplay = ({ results, onStartTrial, onWatchDemo }) => {
  const { timeSavings, costSavings, revenueImpact, roi } = results;

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Format hours
  const formatHours = (hours) => {
    return Math.round(hours).toLocaleString();
  };

  // Determine payback period text
  const getPaybackText = () => {
    if (roi.paybackMonths < 1) {
      return 'Less than 1 month';
    } else if (roi.paybackMonths < 2) {
      return '1 month';
    } else {
      return `${Math.round(roi.paybackMonths)} months`;
    }
  };

  return (
    <Box>
      {/* Header */}
      <Typography
        variant="h3"
        sx={{
          textAlign: 'center',
          mb: 1,
          fontWeight: 700
        }}
      >
        Your Results
      </Typography>
      <Typography
        variant="body1"
        sx={{
          textAlign: 'center',
          color: 'text.secondary',
          mb: 4
        }}
      >
        Here's how Revfer will impact your business
      </Typography>

     {/* Summary Cards - UPDATE THESE */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Time Saved - UPDATED */}
        <Grid item xs={12} md={4}>
            <Paper
            elevation={3}
            sx={{
                p: 3,
                height: '100%',
                borderRadius: 3,
                textAlign: 'center',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white'
            }}
            >
            <TimeIcon sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                {formatHours(timeSavings.total.annual)}
            </Typography>
            <Typography variant="h6" sx={{ mb: 1, opacity: 0.9 }}>
                hours per year
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                That's {Math.round(timeSavings.total.annual / 40)} weeks of full-time work!
            </Typography>
            <Divider sx={{ my: 1, bgcolor: 'rgba(255,255,255,0.2)' }} />
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Including {formatHours(results.socialMedia.savings.time.annual.saved)} hours 
                saved on social media content
            </Typography>
            </Paper>
        </Grid>

        {/* Cost Saved - UPDATED */}
        <Grid item xs={12} md={4}>
            <Paper
            elevation={3}
            sx={{
                p: 3,
                height: '100%',
                borderRadius: 3,
                textAlign: 'center',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white'
            }}
            >
            <MoneyIcon sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                {formatCurrency(costSavings.total.annual)}
            </Typography>
            <Typography variant="h6" sx={{ mb: 1, opacity: 0.9 }}>
                saved per year
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                vs. hiring help & buying separate tools
            </Typography>
            <Divider sx={{ my: 1, bgcolor: 'rgba(255,255,255,0.2)' }} />
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Including {formatCurrency(results.socialMedia.savings.cost.total.annual)} 
                saved on social media
            </Typography>
            </Paper>
        </Grid>

        {/* Revenue Increase - UPDATED */}
        <Grid item xs={12} md={4}>
            <Paper
            elevation={3}
            sx={{
                p: 3,
                height: '100%',
                borderRadius: 3,
                textAlign: 'center',
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white'
            }}
            >
            <RevenueIcon sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                {formatCurrency(revenueImpact.total.annual)}
            </Typography>
            <Typography variant="h6" sx={{ mb: 1, opacity: 0.9 }}>
                revenue increase
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                from ratings, responses & social media
            </Typography>
            <Divider sx={{ my: 1, bgcolor: 'rgba(255,255,255,0.2)' }} />
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Including {formatCurrency(results.socialMedia.revenue.annual)} 
                from consistent social presence
            </Typography>
            </Paper>
        </Grid>
        </Grid>

      {/* ROI Highlight Box */}
      <Paper
        elevation={4}
        sx={{
          p: 4,
          mb: 4,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          border: '2px solid',
          borderColor: 'primary.main'
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                💰 Total Annual Value:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {formatCurrency(roi.totalValue)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                💳 Annual Revfer Cost:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                {formatCurrency(roi.annualCost)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                ✨ Your Net Benefit:
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                {formatCurrency(roi.netBenefit)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                ⏱️ Payback Period:
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'info.main' }}>
                {getPaybackText()}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main', mb: 1 }}>
                📊 ROI: {Math.round(roi.percentage).toLocaleString()}%
              </Typography>
              <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                ({Math.round(roi.multiplier)}x return on investment)
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Detailed Breakdown */}
      <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
          Breakdown
        </Typography>

        {/* Time Savings Details */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimeIcon color="primary" />
            Time Savings Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: '#f5f7fa', borderRadius: 1 }}>
                <Typography variant="body2">Customer service:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatHours(timeSavings.customerService.annual)} hrs/year
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: '#f5f7fa', borderRadius: 1 }}>
                <Typography variant="body2">Content creation:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatHours(timeSavings.content.annual)} hrs/year
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: '#f5f7fa', borderRadius: 1 }}>
                <Typography variant="body2">Review responses:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatHours(timeSavings.reviews.annual)} hrs/year
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Cost Savings Details */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <MoneyIcon color="primary" />
            Cost Savings Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: '#f5f7fa', borderRadius: 1 }}>
                <Typography variant="body2">Labor cost avoided:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatCurrency(costSavings.labor.annual)}/year
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: '#f5f7fa', borderRadius: 1 }}>
                <Typography variant="body2">vs. Separate tools:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatCurrency(costSavings.tools.annual)}/year
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Revenue Impact Details */}
        <Box>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <RevenueIcon color="primary" />
            Revenue Impact Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Box sx={{ p: 1, bgcolor: '#f5f7fa', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Better rating ({revenueImpact.rating.currentRating.toFixed(1)}→{revenueImpact.rating.smartRating}):
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatCurrency(revenueImpact.rating.annual)}/year
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ p: 1, bgcolor: '#f5f7fa', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Faster response time:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatCurrency(revenueImpact.response.annual)}/year
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ p: 1, bgcolor: '#fff3cd', borderRadius: 1, border: '1px solid #ffc107' }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Network discovery*:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatCurrency(revenueImpact.network.annual)}/year
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Alert severity="info" sx={{ mt: 2 }}>
            *Network discovery revenue requires network adoption in your area and is shown as potential additional value.
          </Alert>
        </Box>
      </Paper>
      {/* Social Media Content Generation Details - NEW SECTION */}

      {results.socialMedia.savings.posts.perWeek > 0 ? (
      <>
        <Divider sx={{ my: 3 }} />
        
        <Box>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              component="span"
              sx={{
                fontSize: '1.5rem',
                display: 'inline-flex',
                alignItems: 'center'
              }}
            >
              📱
            </Box>
            Social Media Content Generation
          </Typography>

           {/* Social Media Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, bgcolor: '#e3f2fd', textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 0.5 }}>
                {Math.round(results.socialMedia.savings.posts.annual)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Posts per year
                </Typography>
            </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, bgcolor: '#f3e5f5', textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: 'secondary.main', fontWeight: 700, mb: 0.5 }}>
                {Math.round(results.socialMedia.savings.time.annual.saved)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Hours saved/year
                </Typography>
            </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, bgcolor: '#e8f5e9', textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: 'success.main', fontWeight: 700, mb: 0.5 }}>
                {formatCurrency(results.socialMedia.savings.cost.total.annual)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Cost saved/year
                </Typography>
            </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, bgcolor: '#fff3e0', textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: 'warning.main', fontWeight: 700, mb: 0.5 }}>
                {Math.round(results.socialMedia.savings.efficiency.timeReductionPercent)}%
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Time reduction
                </Typography>
            </Paper>
            </Grid>
        </Grid>

        {/* Time Comparison */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: '#ffebee', borderRadius: 1, border: '1px solid #ef5350' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'error.dark' }}>
                ❌ Without AI (Manual Creation):
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                • {Math.round(results.socialMedia.savings.time.perPost.manual * 60)} minutes per post
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                • {formatHours(results.socialMedia.savings.time.weekly.manual)} hours per week
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                • {formatHours(results.socialMedia.savings.time.annual.manual)} hours per year
                </Typography>
            </Box>
            </Grid>
            <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #4caf50' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'success.dark' }}>
                ✅ With Revfer AI:
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                • {Math.round(results.socialMedia.savings.time.perPost.ai * 60)} minutes per post
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                • {formatHours(results.socialMedia.savings.time.weekly.ai)} hours per week
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                • {formatHours(results.socialMedia.savings.time.annual.ai)} hours per year
                </Typography>
            </Box>
            </Grid>
        </Grid>

        {/* Cost Breakdown */}
        <Box sx={{ p: 2, bgcolor: '#f5f7fa', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            💰 Cost Savings Breakdown:
            </Typography>
            <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                • Time saved (valued at ${CALCULATION_CONFIG.laborCosts.partTimeHourlyRate * CALCULATION_CONFIG.laborCosts.benefitsMultiplier}/hr):
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', ml: 2 }}>
                {formatCurrency(results.socialMedia.savings.cost.labor.annual)}/year
                </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                • vs. Separate tools ($111/month):
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', ml: 2 }}>
                {formatCurrency(results.socialMedia.savings.cost.tools.annual)}/year
                </Typography>
            </Grid>
            </Grid>
        </Box>

        {/* Revenue Impact */}
        <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Revenue Impact from Consistent Social Presence:</strong>
            </Typography>
            <Typography variant="body2">
            Consistent, quality social media content can increase revenue by{' '}
            <strong>{formatCurrency(results.socialMedia.revenue.annual)}/year</strong>{' '}
            ({results.socialMedia.revenue.percentage}% conservative estimate).
            Research shows engaged brands see 20-40% revenue increases.
            </Typography>
        </Alert>

        {/* What AI Does */}
        <Box sx={{ mt: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            🤖 What Revfer's AI Does for Social Media:
            </Typography>
            <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', gap: 0.5 }}>
                <span>•</span>
                <span>Analyzes your customer reviews for content ideas</span>
                </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', gap: 0.5 }}>
                <span>•</span>
                <span>Generates engaging captions in your brand voice</span>
                </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', gap: 0.5 }}>
                <span>•</span>
                <span>Suggests relevant hashtags and posting times</span>
                </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', gap: 0.5 }}>
                <span>•</span>
                <span>Creates multiple content variations instantly</span>
                </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', gap: 0.5 }}>
                <span>•</span>
                <span>Maintains consistent posting schedule</span>
                </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', gap: 0.5 }}>
                <span>•</span>
                <span>Adapts content for different platforms</span>
                </Typography>
            </Grid>
            </Grid>
        </Box>

         
        </Box>
      </>
    ) : (
      <>
        <Divider sx={{ my: 3 }} />
        
        {/* Show opportunity message if not posting */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>💡 Opportunity: Social Media Content Generation</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You're not currently posting on social media. Research shows:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            <li>
              <Typography variant="body2">
                90% of consumers buy from brands they follow on social media
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Businesses with consistent social presence see 20-40% more revenue
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Daily posting (5-7x/week) doubles engagement rates
              </Typography>
            </li>
          </Box>
          <Typography variant="body2" sx={{ mt: 2, fontWeight: 600 }}>
            With Revfer's AI, you could create quality social content in just 6 minutes per post 
            (vs. 37 minutes manually). Adjust the slider above to see potential savings!
          </Typography>
        </Alert>
      </>
    )}
      

       

      {/* Key Takeaways */}
      <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: 3, bgcolor: '#f5f7fa' }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
          Key Takeaways
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <CheckIcon color="success" />
              <Typography variant="body2">
                Save <strong>{formatHours(timeSavings.total.weekly)} hours per week</strong> —
                that's {Math.round(timeSavings.total.weekly / 40 * 100)}% of a full-time employee
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <CheckIcon color="success" />
              <Typography variant="body2">
                Reduce costs by <strong>{formatCurrency(costSavings.total.monthly)}/month</strong> compared
                to hiring part-time help
              </Typography>
            </Box>
          </Grid>
          {/* NEW: Social Media Takeaway */}
            <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 2 }}>
                <CheckIcon color="success" />
                <Typography variant="body2">
                Create <strong>{Math.round(results.socialMedia.savings.posts.annual)} social media posts</strong> per
                year while saving <strong>{formatHours(results.socialMedia.savings.time.annual.saved)} hours</strong>
                </Typography>
            </Box>
            </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <CheckIcon color="success" />
              <Typography variant="body2">
                Improve your rating from <strong>{revenueImpact.rating.currentRating.toFixed(1)} to {revenueImpact.rating.smartRating}</strong> through
                verified network reviews
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <CheckIcon color="success" />
              <Typography variant="body2">
                Generate <strong>{formatCurrency(revenueImpact.total.monthly)}/month</strong> in additional
                revenue from better ratings and faster responses
              </Typography>
            </Box>
          </Grid>
          {/* NEW: Efficiency Takeaway */}
        <Grid item xs={12} md={6}>
        <Box sx={{ display: 'flex', gap: 2 }}>
            <CheckIcon color="success" />
            <Typography variant="body2">
            Reduce social media content creation time by <strong>84%</strong> —
            from {Math.round(results.socialMedia.savings.time.perPost.manual * 60)} minutes to{' '}
            {Math.round(results.socialMedia.savings.time.perPost.ai * 60)} minutes per post
            </Typography>
        </Box>
        </Grid>
        </Grid>
      </Paper>

      {/* CTA Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          size="large"
          onClick={onStartTrial}
          sx={{
            px: 4,
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: 4,
            '&:hover': {
              boxShadow: 8,
              transform: 'translateY(-2px)'
            }
          }}
        >
          Start Free Trial →
        </Button>
        <Button
          variant="outlined"
          size="large"
          onClick={onWatchDemo}
          sx={{
            px: 4,
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 600,
            textTransform: 'none',
            borderWidth: 2,
            '&:hover': {
              borderWidth: 2
            }
          }}
        >
          Watch Demo
        </Button>
      </Box>

      {/* Disclaimer */}
      <Box sx={{ mt: 4, p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          <strong>Note:</strong> These calculations are based on conservative estimates from authoritative
          research sources. Your actual results may vary based on your specific business operations,
          implementation, and engagement. All calculations use peer-reviewed research and government
          statistics. View our complete methodology and data sources above.
        </Typography>
      </Box>
    </Box>
  );
};

export default ResultsDisplay;